<template>
    <section class="mt-10">
        <header class="flex flex-wrap items-end justify-between gap-4">
            <div>
                <h3 class="text-sm font-semibold text-gray-900 dark:text-white">
                    Compras enviadas
                </h3>
                <p
                    class="mt-0.5 max-w-xl text-xs text-gray-500 dark:text-gray-400"
                >
                    O Kraken registra a compra na aprovação da venda. No pedido
                    de reembolso, reembolso ou chargeback, a compra é cancelada
                    e o comprador perde o cashback.
                </p>
            </div>
            <form
                v-if="available !== false"
                class="relative w-full sm:w-72"
                role="search"
                @submit.prevent="fetch(1)"
            >
                <svg
                    class="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    aria-hidden="true"
                >
                    <path
                        fill-rule="evenodd"
                        d="M9 3.5a5.5 5.5 0 100 11 5.5 5.5 0 000-11zM2 9a7 7 0 1112.452 4.391l3.328 3.329a.75.75 0 11-1.06 1.06l-3.329-3.328A7 7 0 012 9z"
                        clip-rule="evenodd"
                    />
                </svg>
                <input
                    v-model="search"
                    type="text"
                    placeholder="Transação, hash, pedido ou e-mail"
                    aria-label="Buscar compras"
                    class="w-full rounded-md border border-gray-300 bg-white py-2 pl-9 pr-8 text-sm text-gray-900 placeholder:text-gray-400 focus:border-gray-900 focus:ring-gray-900 dark:border-gray-600 dark:bg-gray-900 dark:text-white dark:focus:border-white dark:focus:ring-white"
                    @input="debouncedFetch"
                />
                <button
                    v-if="search"
                    type="button"
                    class="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                    aria-label="Limpar busca"
                    @click="clearSearch"
                >
                    <svg
                        class="h-3.5 w-3.5"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                        aria-hidden="true"
                    >
                        <path
                            d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z"
                        />
                    </svg>
                </button>
            </form>
        </header>

        <div
            v-if="available === false"
            class="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-5 py-4 dark:border-amber-500/30 dark:bg-amber-500/10"
        >
            <p class="text-sm font-medium text-amber-800 dark:text-amber-200">
                A tabela de compras ainda não existe neste ambiente.
            </p>
            <p class="mt-1 text-xs text-amber-700 dark:text-amber-300">
                Rode a migration
                <code class="font-mono">create_crm_bonus_purchases_table</code>
                (package-migrations 1.4.40).
            </p>
        </div>

        <template v-else>
            <div
                class="mt-4 grid grid-cols-2 gap-px overflow-hidden rounded-lg border border-gray-200 bg-gray-200 dark:border-gray-700 dark:bg-gray-700 sm:grid-cols-3 lg:grid-cols-6"
            >
                <button
                    v-for="tile in tiles"
                    :key="tile.value"
                    type="button"
                    class="group relative bg-white px-4 pb-3 pt-4 text-left transition-colors hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-gray-900 dark:bg-gray-800 dark:hover:bg-gray-800/60 dark:focus-visible:ring-white"
                    :aria-pressed="status === tile.value"
                    @click="selectStatus(tile.value)"
                >
                    <span
                        class="absolute inset-x-0 top-0 h-0.5 transition-opacity"
                        :class="[
                            tile.rail,
                            status === tile.value
                                ? 'opacity-100'
                                : 'opacity-0 group-hover:opacity-40',
                        ]"
                    ></span>
                    <span
                        class="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400"
                    >
                        <span
                            class="h-1.5 w-1.5 rounded-full"
                            :class="tile.dot"
                        ></span>
                        {{ tile.label }}
                    </span>
                    <span
                        class="mt-1 block text-2xl font-semibold tabular-nums tracking-tight text-gray-900 dark:text-white"
                    >
                        {{ number(count(tile.value)) }}
                    </span>
                    <span
                        class="mt-0.5 block text-[11px] tabular-nums text-gray-400 dark:text-gray-500"
                        >{{ tile.hint }}</span
                    >
                </button>
            </div>

            <div
                class="mt-3 overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800"
            >
                <div class="overflow-x-auto">
                    <table class="min-w-full text-left text-sm">
                        <thead
                            class="bg-gray-50/80 text-[11px] uppercase tracking-wide text-gray-500 dark:bg-gray-900/40 dark:text-gray-400"
                        >
                            <tr>
                                <th class="py-2.5 pl-5 pr-4 font-medium">
                                    Transação
                                </th>
                                <th class="px-4 py-2.5 font-medium">
                                    Produtor
                                </th>
                                <th class="px-4 py-2.5 font-medium">Ciclo</th>
                                <th class="px-4 py-2.5 text-right font-medium">
                                    Campanha
                                </th>
                                <th class="w-10 px-4 py-2.5">
                                    <span class="sr-only">Detalhes</span>
                                </th>
                            </tr>
                        </thead>

                        <tbody
                            v-if="loading"
                            class="divide-y divide-gray-100 dark:divide-gray-700/50"
                            aria-busy="true"
                        >
                            <tr v-for="row in 4" :key="row">
                                <td class="py-4 pl-5 pr-4">
                                    <div
                                        class="h-3 w-40 animate-pulse rounded bg-gray-100 dark:bg-gray-700"
                                    ></div>
                                    <div
                                        class="mt-2 h-2.5 w-24 animate-pulse rounded bg-gray-100 dark:bg-gray-700"
                                    ></div>
                                </td>
                                <td class="px-4 py-4">
                                    <div
                                        class="h-3 w-28 animate-pulse rounded bg-gray-100 dark:bg-gray-700"
                                    ></div>
                                    <div
                                        class="mt-2 h-2.5 w-36 animate-pulse rounded bg-gray-100 dark:bg-gray-700"
                                    ></div>
                                </td>
                                <td class="px-4 py-4">
                                    <div
                                        class="h-3 w-48 animate-pulse rounded bg-gray-100 dark:bg-gray-700"
                                    ></div>
                                </td>
                                <td class="px-4 py-4">
                                    <div
                                        class="ml-auto h-3 w-6 animate-pulse rounded bg-gray-100 dark:bg-gray-700"
                                    ></div>
                                </td>
                                <td></td>
                            </tr>
                        </tbody>

                        <tbody v-else-if="!purchases.length">
                            <tr>
                                <td colspan="5" class="px-5 py-14 text-center">
                                    <p
                                        class="text-sm text-gray-900 dark:text-white"
                                    >
                                        {{
                                            search || status
                                                ? "Nenhuma compra com esse filtro."
                                                : "Nenhuma compra enviada ainda."
                                        }}
                                    </p>
                                    <p
                                        class="mt-1 text-xs text-gray-500 dark:text-gray-400"
                                    >
                                        {{
                                            search || status
                                                ? "Limpe a busca ou escolha outro status."
                                                : "As compras aparecem aqui quando uma venda de seller ativo é aprovada."
                                        }}
                                    </p>
                                </td>
                            </tr>
                        </tbody>

                        <tbody
                            v-else
                            class="divide-y divide-gray-100 dark:divide-gray-700/50"
                        >
                            <template
                                v-for="purchase in purchases"
                                :key="purchase.id"
                            >
                                <tr
                                    class="group align-top text-gray-900 transition-colors hover:bg-gray-50/70 dark:text-white dark:hover:bg-gray-900/30"
                                >
                                    <td class="relative py-3.5 pl-5 pr-4">
                                        <span
                                            class="absolute inset-y-0 left-0 w-0.5"
                                            :class="meta(purchase.status).rail"
                                            aria-hidden="true"
                                        ></span>
                                        <div class="flex items-center gap-1.5">
                                            <span class="font-mono text-xs">{{
                                                purchase.unique_transaction_id
                                            }}</span>
                                            <button
                                                type="button"
                                                class="rounded p-0.5 text-gray-400 opacity-0 transition-opacity hover:text-gray-700 focus:opacity-100 group-hover:opacity-100 dark:hover:text-gray-200"
                                                :aria-label="`Copiar ${purchase.unique_transaction_id}`"
                                                @click="
                                                    copy(
                                                        purchase.unique_transaction_id,
                                                    )
                                                "
                                            >
                                                <svg
                                                    v-if="
                                                        copied ===
                                                        purchase.unique_transaction_id
                                                    "
                                                    class="h-3.5 w-3.5 text-green-600"
                                                    viewBox="0 0 20 20"
                                                    fill="currentColor"
                                                    aria-hidden="true"
                                                >
                                                    <path
                                                        fill-rule="evenodd"
                                                        d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z"
                                                        clip-rule="evenodd"
                                                    />
                                                </svg>
                                                <svg
                                                    v-else
                                                    class="h-3.5 w-3.5"
                                                    viewBox="0 0 20 20"
                                                    fill="currentColor"
                                                    aria-hidden="true"
                                                >
                                                    <path
                                                        d="M7 3.5A1.5 1.5 0 018.5 2h3.879a1.5 1.5 0 011.06.44l3.122 3.12A1.5 1.5 0 0117 6.622V12.5a1.5 1.5 0 01-1.5 1.5h-1v-3.379a3 3 0 00-.879-2.121L10.5 5.379A3 3 0 008.379 4.5H7v-1z"
                                                    />
                                                    <path
                                                        d="M4.5 6A1.5 1.5 0 003 7.5v9A1.5 1.5 0 004.5 18h7a1.5 1.5 0 001.5-1.5v-5.879a1.5 1.5 0 00-.44-1.06L9.44 6.439A1.5 1.5 0 008.378 6H4.5z"
                                                    />
                                                </svg>
                                            </button>
                                        </div>
                                        <p
                                            class="mt-0.5 text-xs tabular-nums text-gray-500 dark:text-gray-400"
                                        >
                                            #{{
                                                purchase.transaction_id ?? "?"
                                            }}
                                            <template v-if="purchase.order_id">
                                                <span
                                                    class="mx-1 text-gray-300 dark:text-gray-600"
                                                    >/</span
                                                >
                                                <router-link
                                                    :to="{
                                                        name: 'user.order',
                                                        params: {
                                                            order_id:
                                                                purchase.order_id,
                                                        },
                                                    }"
                                                    class="underline decoration-gray-300 underline-offset-2 hover:text-gray-900 hover:decoration-gray-900 dark:decoration-gray-600 dark:hover:text-white"
                                                >
                                                    pedido
                                                    {{ purchase.order_id }}
                                                </router-link>
                                            </template>
                                        </p>
                                    </td>

                                    <td class="px-4 py-3.5">
                                        <template v-if="purchase.producer">
                                            <router-link
                                                :to="`/user/${purchase.producer.id}`"
                                                class="text-sm hover:underline hover:underline-offset-2"
                                            >
                                                {{ purchase.producer.name }}
                                            </router-link>
                                            <p
                                                class="mt-0.5 text-xs text-gray-500 dark:text-gray-400"
                                            >
                                                {{ purchase.producer.email }}
                                            </p>
                                        </template>
                                        <span
                                            v-else
                                            class="text-xs text-gray-400"
                                            >Produtor
                                            {{
                                                purchase.producer_id ??
                                                "não informado"
                                            }}</span
                                        >
                                    </td>

                                    <td class="px-4 py-3.5">
                                        <ol
                                            class="flex items-center gap-2 text-xs"
                                        >
                                            <li
                                                class="flex items-center gap-1.5 whitespace-nowrap"
                                                :title="
                                                    fullDate(
                                                        purchase.registered_at ||
                                                            purchase.created_at,
                                                    )
                                                "
                                            >
                                                <span
                                                    class="h-2 w-2 rounded-full ring-2 ring-white dark:ring-gray-800"
                                                    :class="
                                                        purchase.status ===
                                                        'no_campaign'
                                                            ? 'bg-amber-500'
                                                            : purchase.status ===
                                                                'scheduled'
                                                              ? 'bg-blue-500'
                                                              : purchase.status ===
                                                                  'limit_reached'
                                                                ? 'bg-purple-500'
                                                                : 'bg-green-500'
                                                    "
                                                ></span>
                                                <span
                                                    class="text-gray-700 dark:text-gray-200"
                                                    >{{
                                                        purchase.status ===
                                                        "no_campaign"
                                                            ? "Sem campanha"
                                                            : purchase.status ===
                                                                "scheduled"
                                                              ? "Agendada"
                                                              : purchase.status ===
                                                                  "limit_reached"
                                                                ? "Limite do CPF"
                                                                : "Registrada"
                                                    }}</span
                                                >
                                                <span
                                                    class="tabular-nums text-gray-400"
                                                    >{{
                                                        relative(
                                                            purchase.registered_at ||
                                                                purchase.created_at,
                                                        )
                                                    }}</span
                                                >
                                            </li>
                                            <template
                                                v-if="
                                                    purchase.status ===
                                                    'cancelled'
                                                "
                                            >
                                                <li
                                                    class="h-px w-6 shrink-0 bg-gray-300 dark:bg-gray-600"
                                                    aria-hidden="true"
                                                ></li>
                                                <li
                                                    class="flex items-center gap-1.5 whitespace-nowrap"
                                                    :title="
                                                        fullDate(
                                                            purchase.cancelled_at,
                                                        )
                                                    "
                                                >
                                                    <span
                                                        class="h-2 w-2 rounded-full bg-gray-400 ring-2 ring-white dark:ring-gray-800"
                                                    ></span>
                                                    <span
                                                        class="text-gray-700 dark:text-gray-200"
                                                        >Cancelada</span
                                                    >
                                                    <span
                                                        class="tabular-nums text-gray-400"
                                                        >{{
                                                            gap(
                                                                purchase.registered_at,
                                                                purchase.cancelled_at,
                                                            )
                                                        }}</span
                                                    >
                                                </li>
                                            </template>
                                        </ol>
                                    </td>

                                    <td
                                        class="px-4 py-3.5 text-right text-xs tabular-nums text-gray-700 dark:text-gray-300"
                                    >
                                        {{ purchase.campaign_id ?? "—" }}
                                    </td>

                                    <td class="px-4 py-3.5 text-right">
                                        <button
                                            type="button"
                                            class="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-700 dark:hover:text-gray-200"
                                            :aria-expanded="
                                                expanded === purchase.id
                                            "
                                            :aria-label="
                                                expanded === purchase.id
                                                    ? 'Ocultar detalhes'
                                                    : 'Ver detalhes'
                                            "
                                            @click="
                                                expanded =
                                                    expanded === purchase.id
                                                        ? null
                                                        : purchase.id
                                            "
                                        >
                                            <svg
                                                class="h-4 w-4 transition-transform"
                                                :class="{
                                                    'rotate-180':
                                                        expanded ===
                                                        purchase.id,
                                                }"
                                                viewBox="0 0 20 20"
                                                fill="currentColor"
                                                aria-hidden="true"
                                            >
                                                <path
                                                    fill-rule="evenodd"
                                                    d="M5.22 8.22a.75.75 0 011.06 0L10 11.94l3.72-3.72a.75.75 0 111.06 1.06l-4.25 4.25a.75.75 0 01-1.06 0L5.22 9.28a.75.75 0 010-1.06z"
                                                    clip-rule="evenodd"
                                                />
                                            </svg>
                                        </button>
                                    </td>
                                </tr>

                                <tr
                                    v-if="expanded === purchase.id"
                                    class="bg-gray-50/70 dark:bg-gray-900/40"
                                >
                                    <td colspan="5" class="px-5 py-4">
                                        <div
                                            class="grid gap-4 lg:grid-cols-[minmax(0,16rem)_minmax(0,1fr)]"
                                        >
                                            <dl
                                                class="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-xs"
                                            >
                                                <template
                                                    v-for="field in details(
                                                        purchase,
                                                    )"
                                                    :key="field.label"
                                                >
                                                    <dt
                                                        class="text-gray-500 dark:text-gray-400"
                                                    >
                                                        {{ field.label }}
                                                    </dt>
                                                    <dd
                                                        class="truncate font-mono text-gray-900 dark:text-white"
                                                        :title="field.value"
                                                    >
                                                        {{ field.value }}
                                                    </dd>
                                                </template>
                                            </dl>
                                            <div>
                                                <p
                                                    class="mb-1.5 text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400"
                                                >
                                                    Última resposta da CRM Bonus
                                                </p>
                                                <pre
                                                    class="max-h-56 overflow-auto rounded-md border border-gray-200 bg-white p-3 font-mono text-xs leading-relaxed text-gray-800 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200"
                                                    >{{
                                                        purchase.response
                                                            ? JSON.stringify(
                                                                  purchase.response,
                                                                  null,
                                                                  2,
                                                              )
                                                            : "Sem resposta gravada."
                                                    }}</pre
                                                >
                                            </div>
                                        </div>
                                    </td>
                                </tr>
                            </template>
                        </tbody>
                    </table>
                </div>

                <footer
                    v-if="pagination && pagination.total"
                    class="flex flex-wrap items-center justify-between gap-3 border-t border-gray-100 px-5 py-2.5 text-xs text-gray-500 dark:border-gray-700/50 dark:text-gray-400"
                >
                    <span class="tabular-nums"
                        >{{ pagination.from }} a {{ pagination.to }} de
                        {{ number(pagination.total) }}</span
                    >
                    <Pagination
                        v-if="pagination.last_page > 1"
                        :pagination="pagination"
                        @paginate="fetch"
                    />
                </footer>
            </div>
        </template>
    </section>
</template>

<script>
import { useToast } from "vue-toastification";
import Pagination from "../layouts/Pagination.vue";

const STATUSES = {
    registered: {
        label: "Registradas",
        rail: "bg-green-500",
        dot: "bg-green-500",
    },
    cancelled: {
        label: "Canceladas",
        rail: "bg-gray-400 dark:bg-gray-500",
        dot: "bg-gray-400",
    },
    no_campaign: {
        label: "Sem campanha",
        rail: "bg-amber-500",
        dot: "bg-amber-500",
    },
    scheduled: {
        label: "Agendadas",
        rail: "bg-blue-500",
        dot: "bg-blue-500",
    },
    limit_reached: {
        label: "Limite do CPF",
        rail: "bg-purple-500",
        dot: "bg-purple-500",
    },
};

const SEARCH_DELAY_MS = 350;

export default {
    components: { Pagination },
    setup() {
        return { toast: useToast() };
    },
    data() {
        return {
            loading: false,
            available: null,
            purchases: [],
            pagination: null,
            counts: {},
            status: "",
            search: "",
            expanded: null,
            copied: null,
            searchTimer: null,
            requestId: 0,
        };
    },
    computed: {
        total() {
            return Object.values(this.counts).reduce(
                (sum, value) => sum + value,
                0,
            );
        },
        tiles() {
            const sent = this.count("registered") + this.count("cancelled");
            const cancelRate = sent
                ? Math.round((this.count("cancelled") / sent) * 100)
                : 0;

            return [
                {
                    value: "",
                    label: "Todas",
                    rail: "bg-gray-900 dark:bg-white",
                    dot: "bg-gray-900 dark:bg-white",
                    hint: "enviadas para a CRM Bonus",
                },
                {
                    value: "registered",
                    ...STATUSES.registered,
                    hint: "cashback ativo",
                },
                {
                    value: "cancelled",
                    ...STATUSES.cancelled,
                    hint: sent
                        ? `${cancelRate}% das registradas`
                        : "nenhuma ainda",
                },
                {
                    value: "no_campaign",
                    ...STATUSES.no_campaign,
                    hint: "sem campanha ativa na hora",
                },
                {
                    value: "scheduled",
                    ...STATUSES.scheduled,
                    hint: "aguardando o prazo de envio",
                },
                {
                    value: "limit_reached",
                    ...STATUSES.limit_reached,
                    hint: "CPF já ganhou o vale no mês, sem taxa",
                },
            ];
        },
    },
    mounted() {
        this.fetch(1);
    },
    beforeUnmount() {
        clearTimeout(this.searchTimer);
    },
    methods: {
        count(status) {
            return status ? (this.counts[status] ?? 0) : this.total;
        },
        meta(status) {
            return STATUSES[status] ?? { rail: "bg-gray-300" };
        },
        number(value) {
            return Number(value).toLocaleString("pt-BR");
        },
        fullDate(value) {
            return value
                ? new Date(value).toLocaleString("pt-BR", {
                      dateStyle: "short",
                      timeStyle: "medium",
                  })
                : "";
        },
        relative(value) {
            if (!value) {
                return "";
            }

            const minutes = Math.round(
                (Date.now() - new Date(value).getTime()) / 60000,
            );

            if (minutes < 1) {
                return "agora";
            }

            if (minutes < 60) {
                return `há ${minutes} min`;
            }

            if (minutes < 60 * 24) {
                return `há ${Math.round(minutes / 60)} h`;
            }

            return new Date(value).toLocaleDateString("pt-BR", {
                day: "2-digit",
                month: "2-digit",
                year: "2-digit",
            });
        },
        gap(from, to) {
            if (!from || !to) {
                return "";
            }

            const minutes = Math.max(
                Math.round((new Date(to) - new Date(from)) / 60000),
                0,
            );

            if (minutes < 60) {
                return `${minutes} min depois`;
            }

            if (minutes < 60 * 48) {
                return `${Math.round(minutes / 60)} h depois`;
            }

            return `${Math.round(minutes / 1440)} dias depois`;
        },
        details(purchase) {
            return [
                { label: "Registro", value: purchase.id },
                { label: "Produtor", value: purchase.producer_id ?? "—" },
                { label: "trace_id", value: purchase.trace_id ?? "—" },
                { label: "purchase_id", value: purchase.purchase_id ?? "—" },
                {
                    label: "Registrada",
                    value: this.fullDate(purchase.registered_at) || "—",
                },
                {
                    label: "Cancelada",
                    value: this.fullDate(purchase.cancelled_at) || "—",
                },
            ].map((field) => ({ ...field, value: String(field.value) }));
        },
        copy(value) {
            navigator.clipboard
                ?.writeText(value)
                .then(() => {
                    this.copied = value;
                    setTimeout(
                        () =>
                            (this.copied =
                                this.copied === value ? null : this.copied),
                        1500,
                    );
                })
                .catch(() => this.toast.error("Não foi possível copiar."));
        },
        selectStatus(status) {
            this.status = this.status === status ? "" : status;
            this.fetch(1);
        },
        debouncedFetch() {
            clearTimeout(this.searchTimer);
            this.searchTimer = setTimeout(() => this.fetch(1), SEARCH_DELAY_MS);
        },
        clearSearch() {
            this.search = "";
            this.fetch(1);
        },
        fetch(page = 1) {
            clearTimeout(this.searchTimer);
            // Uma busca lenta nao sobrescreve a resposta de uma busca mais nova.
            const requestId = ++this.requestId;
            this.loading = true;
            this.expanded = null;
            axios
                .get("/api/services/crm-bonus/purchases", {
                    params: {
                        page,
                        status: this.status || undefined,
                        search: this.search.trim() || undefined,
                    },
                })
                .then(({ data }) => {
                    if (requestId !== this.requestId) {
                        return;
                    }

                    this.available = data.available;
                    this.counts = Array.isArray(data.counts) ? {} : data.counts;
                    this.purchases = data.data;
                    this.pagination = data.available ? data : null;
                })
                .catch(() =>
                    this.toast.error(
                        "Não foi possível carregar as compras do CRM Bonus.",
                    ),
                )
                .finally(() => {
                    if (requestId === this.requestId) {
                        this.loading = false;
                    }
                });
        },
    },
};
</script>
